import React, { useState } from "react";
import { Message } from "../types";
import { Bot, User } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Code, CopyBlock, dracula } from "react-code-blocks";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";

interface MessageBubbleProps {
  message: Message;
  showTyping?: boolean;
}

// Collapsible for <think> blocks
const ThinkCollapsible: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className='my-2'>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <Button variant='outline' size='sm' className='text-xs font-mono'>
            {open ? "Hide" : "Show"}{" "}
            <span className='font-bold ml-1'>AI Thinking</span>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className='mt-2 p-2 border-l-4 border-primary-500 bg-muted/50 text-sm whitespace-pre-wrap'>
            {children}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

// Custom renderer for code blocks
const MarkdownCodeBlock = ({ inline, className, children }: any) => {
  const match = /language-(\w+)/.exec(className || "");
  if (inline) {
    return (
      <Code
        text={String(children)}
        language={match ? match[1] : ""}
        theme={dracula}
      />
    );
  }
  return (
    <CopyBlock
      text={String(children).replace(/\n$/, "")}
      language={match ? match[1] : ""}
      showLineNumbers={true}
      theme={dracula}
      wrapLongLines
    />
  );
};

// Custom renderer for <think> blocks
function extractThinkBlocks(content: string) {
  // Returns: { main: string, thinks: string[] }
  const thinkRegex = /<think>([\s\S]*?)<\/think>/g;
  let match;
  // let lastIndex = 0;
  const thinks: string[] = [];
  let main = "";
  let lastEnd = 0;

  // First, handle complete <think> blocks
  while ((match = thinkRegex.exec(content))) {
    main += content.slice(lastEnd, match.index);
    thinks.push(match[1].trim());
    lastEnd = match.index + match[0].length;
  }

  // Then handle incomplete <think> blocks (auto-close them for display)
  const remainingContent = content.slice(lastEnd);
  const incompleteThinkStart = remainingContent.lastIndexOf("<think>");
  if (incompleteThinkStart !== -1) {
    const incompleteThinkContent = remainingContent.slice(
      incompleteThinkStart + 7
    );
    main += remainingContent.slice(0, incompleteThinkStart);
    thinks.push(incompleteThinkContent.trim());
  } else {
    main += remainingContent;
  }

  return { main: main.trim(), thinks };
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  showTyping,
}) => {
  const isUser = message.role === "user";
  const isAssistantEmpty =
    message.role === "assistant" && message.content === "";

  // Extract <think> blocks
  const { main, thinks } = extractThinkBlocks(message.content);

  return (
    <div
      className={`flex items-start space-x-3 ${
        isUser ? "flex-row-reverse space-x-reverse" : ""
      }`}
    >
      <div className='shrink-0'>
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isUser ? "bg-primary" : "bg-muted"
          }`}
        >
          {isUser ? (
            <User className='h-4 w-4 text-primary-foreground' />
          ) : (
            <Bot className='h-4 w-4 text-muted-foreground' />
          )}
        </div>
      </div>

      <div className={`flex-1 max-w-[80%] ${isUser ? "text-right" : ""}`}>
        <Card>
          <CardContent>
            <div className='prose'>
              {/* Collapsible <think> blocks */}
              {thinks.map((think, i) => (
                <ThinkCollapsible key={i}>{think}</ThinkCollapsible>
              ))}
              {/* Main markdown content */}
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code: MarkdownCodeBlock,
                }}
              >
                {main}
              </ReactMarkdown>
              {/* Typing indicator if streaming */}
              {isAssistantEmpty && showTyping && (
                <span className='inline-flex ml-2 align-middle'>
                  <span className='w-2 h-2 bg-muted-foreground rounded-full animate-bounce inline-block'></span>
                  <span
                    className='w-2 h-2 bg-muted-foreground rounded-full animate-bounce inline-block'
                    style={{ animationDelay: "0.1s" }}
                  ></span>
                  <span
                    className='w-2 h-2 bg-muted-foreground rounded-full animate-bounce inline-block'
                    style={{ animationDelay: "0.2s" }}
                  ></span>
                </span>
              )}
            </div>
          </CardContent>
        </Card>
        <div
          className={`text-xs text-muted-foreground mt-1 ${
            isUser ? "text-right" : "text-left"
          }`}
        >
          {message.timestamp.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
    </div>
  );
};
